import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Switch } from "@/components/ui/switch";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, Bell, Mail, MessageSquare } from "lucide-react";
import { NotificationConfig, DEFAULT_CONFIG, DEFAULT_SETTINGS } from "@/types/notificationConfig";
import { AttendanceNotificationSettings } from './AttendanceNotificationSettings';
import { NotificationSoundSettings } from './NotificationSoundSettings';
import { getNotificationTemplates, setNotificationTemplate, NotificationTemplateType, NotificationTemplate } from "@/utils/notifications/templates";

interface NotificationSettingsProps {
  onSave?: () => void;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ onSave }) => {
  const { toast: uiToast } = useToast();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [activeTab, setActiveTab] = useState("payment");
  const [templates, setTemplates] = useState<Record<NotificationTemplateType, NotificationTemplate>>(getNotificationTemplates());

  const displayChannels = (channels: string[] | undefined) => {
    if (!channels) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-1 mb-2">
        {channels.includes('push') && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">App</span>}
        {channels.includes('email') && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Email</span>}
        {channels.includes('sms') && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">SMS</span>}
      </div>
    )
  };

  const handleConfigChange = (category: keyof typeof settings, field: keyof NotificationConfig, value: any) => {
    setSettings((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const handleFrequencyChange = (category: keyof typeof settings, value: string) => {
    if (value === 'immediate' || value === 'daily' || value === 'weekly') {
      handleConfigChange(category, 'frequency', value);
    }
  };

  const handleSave = () => {
    localStorage.setItem('notification_settings', JSON.stringify(settings));
    
    toast.success("Your notification preferences have been updated.");
    
    if (onSave) {
      onSave();
    }
  };

  useEffect(() => {
    const savedSettings = localStorage.getItem('notification_settings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings(parsedSettings);
      } catch (e) {
        console.error("Error parsing saved notification settings");
      }
    }
    setTemplates(getNotificationTemplates());
  }, []);

  const handleTemplateChange = (
    type: NotificationTemplateType,
    field: keyof NotificationTemplate,
    value: string
  ) => {
    const updated = {
      ...templates,
      [type]: {
        ...templates[type],
        [field]: value,
      }
    };
    setTemplates(updated);
    setNotificationTemplate(type, updated[type]);
  };

  const renderTemplateEditor = (category: NotificationTemplateType) => {
    const template = templates[category];
    return (
      <div className="mt-4 p-4 border bg-muted/50 rounded">
        <h4 className="text-sm font-semibold mb-2">Notification Template</h4>
        {displayChannels(template?.channels)}
        <Label>Subject</Label>
        <Input
          value={template?.subject || ''}
          onChange={e => handleTemplateChange(category, "subject", e.target.value)}
          className="mb-2"
        />
        <Label>Body</Label>
        <textarea
          value={template?.body || ''}
          onChange={e => handleTemplateChange(category, "body", e.target.value)}
          className="w-full min-h-[64px] border rounded p-2 text-sm"
        />
        <div className="text-xs text-muted-foreground mt-2">
          You can use variables like <code>{"{{name}}"}</code>, <code>{"{{program}}"}</code>, etc.<br />
          {template?.escalationNote && (
            <span className="block mt-1 text-yellow-800">{template.escalationNote}</span>
          )}
        </div>
      </div>
    );
  };

  const renderConfigControls = (category: keyof typeof settings) => {
    const config = settings[category];
    
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Label htmlFor={`${category}-enabled`}>Enable Notifications</Label>
          <Switch 
            id={`${category}-enabled`}
            checked={config.enabled}
            onCheckedChange={(checked) => handleConfigChange(category, 'enabled', checked)}
          />
        </div>
        
        {config.enabled && (
          <>
            <div className="space-y-4">
              <Label>Notification Channels</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch 
                    id={`${category}-push`}
                    checked={config.pushEnabled}
                    onCheckedChange={(checked) => handleConfigChange(category, 'pushEnabled', checked)}
                  />
                  <Label htmlFor={`${category}-push`}><Bell className="h-4 w-4 inline mr-1" /> App Notifications</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch 
                    id={`${category}-email`}
                    checked={config.emailEnabled}
                    onCheckedChange={(checked) => handleConfigChange(category, 'emailEnabled', checked)}
                  />
                  <Label htmlFor={`${category}-email`}><Mail className="h-4 w-4 inline mr-1" /> Email</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch 
                    id={`${category}-sms`}
                    checked={config.smsEnabled}
                    onCheckedChange={(checked) => handleConfigChange(category, 'smsEnabled', checked)}
                  />
                  <Label htmlFor={`${category}-sms`}><MessageSquare className="h-4 w-4 inline mr-1" /> SMS</Label>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <Label htmlFor={`${category}-frequency`}>Notification Frequency</Label>
              <Select 
                value={config.frequency} 
                onValueChange={(value) => handleFrequencyChange(category, value)}
              >
                <SelectTrigger id={`${category}-frequency`}>
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immediate</SelectItem>
                  <SelectItem value="daily">Daily Digest</SelectItem>
                  <SelectItem value="weekly">Weekly Summary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {category === 'attendanceAlerts' && activeTab === 'attendance' && (
              <div className="space-y-3 mt-6">
                <Label htmlFor="attendance-threshold">Alert Threshold (absences)</Label>
                <Input 
                  id="attendance-threshold"
                  type="number" 
                  min={1}
                  max={10}
                  value={config.threshold || DEFAULT_CONFIG.threshold || 3} 
                  onChange={(e) => handleConfigChange(category, 'threshold', parseInt(e.target.value) || 3)}
                  className="w-20"
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Notification Settings</CardTitle>
        <CardDescription>Configure how you want to receive notifications</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-2 md:grid-cols-6 mb-6">
            <TabsTrigger value="payment">Payment</TabsTrigger>
            <TabsTrigger value="document">Documents</TabsTrigger>
            <TabsTrigger value="attendance">Attendance</TabsTrigger>
            <TabsTrigger value="exam">Exams</TabsTrigger>
            <TabsTrigger value="program">Programs</TabsTrigger>
            <TabsTrigger value="sounds">Sounds</TabsTrigger>
          </TabsList>
          
          <TabsContent value="payment" className="space-y-4">
            <h3 className="text-lg font-medium">Payment Reminder Settings</h3>
            {renderConfigControls('paymentReminders')}
            {renderTemplateEditor('payment')}
          </TabsContent>
          
          <TabsContent value="document" className="space-y-4">
            <h3 className="text-lg font-medium">Document Expiry Settings</h3>
            {renderConfigControls('documentExpiry')}
            {renderTemplateEditor('document')}
          </TabsContent>
          
          <TabsContent value="attendance" className="space-y-4">
            <h3 className="text-lg font-medium">Attendance Alert Settings</h3>
            {renderConfigControls('attendanceAlerts')}
            <Separator className="my-6" />
            <h3 className="text-lg font-medium mb-4">Advanced Attendance Settings</h3>
            <AttendanceNotificationSettings />
            {renderTemplateEditor('attendance')}
          </TabsContent>
          
          <TabsContent value="exam" className="space-y-4">
            <h3 className="text-lg font-medium">Exam Results Settings</h3>
            {renderConfigControls('examResults')}
            {renderTemplateEditor('exam')}
          </TabsContent>
          
          <TabsContent value="program" className="space-y-4">
            <h3 className="text-lg font-medium">Program Update Settings</h3>
            {renderConfigControls('programUpdates')}
            {renderTemplateEditor('program')}
          </TabsContent>
          
          <TabsContent value="sounds" className="space-y-4">
            <h3 className="text-lg font-medium">Notification Sound Settings</h3>
            <NotificationSoundSettings />
          </TabsContent>
        </Tabs>
        
        <Separator className="my-6" />
        
        <div className="flex justify-end">
          <Button onClick={handleSave}>Save Settings</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
